import DndIcon from "../../../../../Assets/Icons/dnd-icon.svg";
import EditIcon from "../../../../../Assets/Icons/edit-icon.svg";
import CloseOutline from "../../../../../Assets/Icons/close-outline.svg";
import CheckIcon from "../../../../../Assets/Icons/check.svg";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import {
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Draggable } from "@hello-pangea/dnd";

const ChatbotAccordionTimelineItem = ({
  uniqueId,
  index,
  item,
  isParsing,
  disableAction,
  isWeddingDaySchedule,
  onMutateItem,
}) => {
  const [itemName, setItemName] = useState(
    isWeddingDaySchedule ? item.event : item.task
  );
  const [itemRelativeTime, setItemRelativeTime] = useState(
    isWeddingDaySchedule ? item.time : item.relative_time
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingMutation, setIsLoadingMutation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDone, setIsDone] = useState(item?.done ?? false);

  const handleClickEdit = useCallback(() => {
    if (isParsing || disableAction) {
      return;
    }

    setItemName(isWeddingDaySchedule ? item.event : item.task);
    setItemRelativeTime(isWeddingDaySchedule ? item.time : item.relative_time);
    setIsEditing(true);
  }, [isParsing, disableAction, isWeddingDaySchedule, item]);

  const handleClickClose = async () => {
    if (isParsing || disableAction || isLoadingMutation) {
      return;
    }

    if (isEditing) {
      setIsEditing(false);
      return;
    }

    setIsDeleting(true);
    await onMutateItem({ type: "delete", item });
    setIsDeleting(false);
  };

  const handleClickDone = async () => {
    if (isParsing) {
      return;
    }

    setIsLoadingMutation(true);
    setIsDone(!isDone);

    await onMutateItem({
      type: "done",
      item: {
        ...item,
        done: !isDone,
      },
    });

    setIsLoadingMutation(false);
  };

  useEffect(() => {
    setIsDone(item?.done ?? false);
  }, [item?.done]);

  const renderTextContent = useMemo(() => {
    if (isEditing) {
      return (
        <>
          <TextField
            sx={{
              flex: 3,
            }}
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            variant="standard"
            hiddenLabel
          />

          <TextField
            sx={{
              flex: 1,
            }}
            value={itemRelativeTime}
            onChange={(e) => setItemRelativeTime(e.target.value)}
            variant="standard"
            hiddenLabel
          />
        </>
      );
    }

    return (
      <>
        <Typography
          component="span"
          sx={{
            color: "#171717",
            textDecoration: isDone ? "line-through" : "none",
            fontWeight: 400,
          }}
        >
          {isWeddingDaySchedule ? item.event : item.task}
        </Typography>

        {itemRelativeTime !== undefined && (
          <Typography
            component="span"
            sx={{
              textDecoration: isDone ? "line-through" : "none",
              fontWeight: 300,
            }}
          >
            {isWeddingDaySchedule ? item.time : item.relative_time}
          </Typography>
        )}
      </>
    );
  }, [
    isEditing,
    isWeddingDaySchedule,
    isDone,
    item.event,
    item.relative_time,
    item.task,
    item.time,
    itemName,
    itemRelativeTime,
  ]);

  const renderLeftActions = useMemo(() => {
    if (isEditing) {
      if (isLoadingMutation) {
        return <CircularProgress size={20} />;
      }

      return (
        <img
          src={CheckIcon}
          alt="check-icon"
          style={{ cursor: "pointer", width: 20, height: 20 }}
          onClick={async () => {
            if (isLoadingMutation) {
              return;
            }

            setIsLoadingMutation(true);

            await onMutateItem({
              type: "edit",
              item: {
                ...item,
                ...(isWeddingDaySchedule
                  ? { event: itemName, time: itemRelativeTime }
                  : { task: itemName, relative_time: itemRelativeTime }),
              },
            });

            setIsLoadingMutation(false);
            setIsEditing(false);
          }}
        />
      );
    }

    return (
      <img
        src={EditIcon}
        alt="edit-icon"
        style={{ cursor: "pointer" }}
        onClick={handleClickEdit}
      />
    );
  }, [
    isEditing,
    handleClickEdit,
    isLoadingMutation,
    onMutateItem,
    item,
    isWeddingDaySchedule,
    itemName,
    itemRelativeTime,
  ]);

  useEffect(() => {
    const submitWithEnter = async (event) => {
      if (!isEditing) return;

      if (event.key === "Enter") {
        setIsLoadingMutation(true);

        await onMutateItem({
          type: "edit",
          item: {
            ...item,
            task: itemName,
            relative_time: itemRelativeTime,
          },
        });

        setIsLoadingMutation(false);
        setIsEditing(false);
      }
    };

    document.addEventListener("keydown", submitWithEnter);

    return () => {
      document.removeEventListener("keydown", submitWithEnter);
    };
  }, [isEditing, item, itemName, itemRelativeTime, onMutateItem]);

  return (
    <Draggable
      draggableId={uniqueId}
      index={index}
      isDragDisabled={isParsing || disableAction}
    >
      {(provided) => (
        <div
          className="chatbot-accordion__content-container"
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <div className="chatbot-accordion__content">
            <img src={DndIcon} alt="dnd-icon" />
            <Checkbox
              icon={<RadioButtonUncheckedOutlinedIcon />}
              checkedIcon={
                <CheckCircleOutlineOutlinedIcon sx={{ color: "#171717" }} />
              }
              checked={isDone}
              onChange={handleClickDone}
              disabled={isParsing || disableAction}
              sx={{
                width: 24,
                height: 24,
                color: "#171717",
              }}
            />
            {/* <img
              src={isDone ? CircleCheckedIcon : CircleIcon}
              alt="circle-icon"
              style={{ cursor: "pointer" }}
              onClick={handleClickDone}
            /> */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                rowGap: 4,
              }}
            >
              {renderTextContent}
            </div>
          </div>
          <div className="chatbot-accordion__content-actions">
            {renderLeftActions}
            {isDeleting ? (
              <CircularProgress size={20} />
            ) : (
              <img
                src={CloseOutline}
                alt="close-outline-icon"
                style={{ cursor: "pointer", width: 20, height: 20 }}
                onClick={handleClickClose}
              />
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default ChatbotAccordionTimelineItem;
